import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useConfig, useTheme } from '@connor-music/shared';
import { ShieldCheck } from 'lucide-react';

export default function Login() {
  const { isAuthenticated, login } = useAuth();
  const { config } = useConfig();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();

  const logoUrl = resolvedTheme === 'dark' ? config.logo_dark_url : config.logo_light_url;

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8 sm:py-12">
      {/* Card */}
      <div className="w-full max-w-sm sm:max-w-md">
        <div className="rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 px-6 py-8 sm:px-10 sm:py-10">
          {/* Logo & Header */}
          <div className="text-center">
            {logoUrl ? (
              <div className="mb-6 flex justify-center">
                <img
                  src={logoUrl}
                  alt={config.portal_name || 'Connor Music Audit'}
                  style={{ width: config.login_logo_width || 200 }}
                  className="h-auto"
                />
              </div>
            ) : (
              <div className="mb-6 flex justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/50">
                  <ShieldCheck className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
            )}
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
              {config.portal_name || 'Connor Music Audit'}
            </h1>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Employee Portal
            </p>
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-gray-200 dark:border-gray-700" />

          {/* Access notice */}
          <div className="mb-6 rounded-lg bg-gray-50 dark:bg-gray-700/50 px-4 py-3">
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              This portal is restricted to authorized Connor Consulting employees.
              Sign in using your organizational Microsoft account.
            </p>
          </div>

          {/* Sign-in button */}
          <button
            onClick={() => login()}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-accent-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft Entra
          </button>
        </div>

        {/* Footer links */}
        <div className="mt-6 flex flex-col items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          {(config.terms_url || config.privacy_url) && (
            <div className="flex items-center gap-3">
              {config.terms_url && (
                <a href={config.terms_url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  Terms of Use
                </a>
              )}
              {config.terms_url && config.privacy_url && (
                <span aria-hidden="true">&middot;</span>
              )}
              {config.privacy_url && (
                <a href={config.privacy_url} target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
                  Privacy Policy
                </a>
              )}
            </div>
          )}
          <p>&copy; {new Date().getFullYear()} Connor Consulting Corporation</p>
        </div>
      </div>
    </div>
  );
}
